import { useMemo } from "react";

import type { UseTourOptions } from "@frontend/components/organisms/Tour";
import { useLingui } from "@lingui/react/macro";

const HOST = "rwc-project-list";

const target = (selector: string) => `${HOST} >>> ${selector}`;

/** The drawer is portaled inside the island, so it hangs off the same host. */
const DRAWER = target('[data-slot="drawer-popup"]');

/**
 * What the quickview is worth pointing at, the first time a row opens it: the
 * row it follows, the keys that move it, the page they cross, the way out.
 *
 * The same four stops the legacy subscriptions page walks through.
 */
export function useProjectQuickviewTour(): UseTourOptions {
  const { t } = useLingui();

  return useMemo(
    () => ({
      storageKey: "project-quickview-v1",
      steps: [
        {
          icon: "MousePointerClick",
          target: target('[data-slot="data-table-row"][data-active]'),
          clip: DRAWER,
          side: "right",
          title: t`The row you are on`,
          content: t`The open row stays highlighted in the table, so you always know where you are in the list.`,
        },
        {
          icon: "ArrowUpDown",
          target: DRAWER,
          side: "left",
          keys: ["↑", "↓"],
          title: t`Keyboard navigation`,
          content: t`Move from one project to the next without closing the panel. A field you are typing in keeps its own arrows.`,
        },
        {
          icon: "ChevronsLeftRight",
          target: target('[data-slot="data-table-row"]:last-child'),
          clip: DRAWER,
          side: "right",
          title: t`Crossing pages`,
          content: t`From this last row, the down arrow loads the next page and opens its first row. The up arrow goes back the same way.`,
        },
        {
          icon: "X",
          target: target('[data-slot="drawer-close"]'),
          side: "bottom",
          align: "end",
          keys: ["Esc"],
          title: t`Closing the panel`,
          content: t`This button closes the panel. The Escape key does the same, without leaving the keyboard.`,
        },
      ],
    }),
    [t],
  );
}
